import type { V2VersionChange } from '@idc-forms/schemas/api-v2/admin-entity-versions'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { formatVersionValue, labelForPath } from '../version-field-labels'

type VersionChangesProps = {
  changes: V2VersionChange[]
  className?: string
}

/**
 * Los campos que cambiaron en una versión.
 *
 * Muestra el antes y el después de cada uno, con el nombre que la persona ve en el formulario
 * y no la ruta del documento (ver `version-field-labels`). Los valores compuestos —objetos y
 * listas— no se vuelcan: se dice qué son y cuántos elementos tienen, porque un JSON crudo en
 * una fila de historial no lo lee nadie.
 *
 * Una lista VACÍA es un resultado legítimo y se dice con todas las letras: significa que la
 * escritura no modificó ningún campo con contenido —guardar el formulario sin tocar nada, o una
 * edición que sólo movió metadatos—. Dejar el bloque en blanco haría pensar que la vista falló.
 */
export function VersionChanges({ changes, className }: VersionChangesProps) {
  if (changes.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        Esta versión no modificó ningún campo del expediente.
      </p>
    )
  }

  return (
    <ul className={cn('flex flex-col gap-2', className)}>
      {changes.map((change) => (
        <li
          key={change.path}
          className="rounded-md border bg-muted/30 px-3 py-2 text-sm dark:bg-muted/10"
        >
          <p className="font-medium">{labelForPath(change.path)}</p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            <span
              className={cn(
                'rounded px-1.5 py-0.5',
                change.kind === 'added'
                  ? 'text-muted-foreground'
                  : 'bg-destructive/10 text-destructive line-through decoration-destructive/40'
              )}
            >
              {formatVersionValue(change.before)}
            </span>
            <ArrowRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />
            <span
              className={cn(
                'rounded px-1.5 py-0.5',
                change.kind === 'removed'
                  ? 'text-muted-foreground'
                  : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              )}
            >
              {formatVersionValue(change.after)}
            </span>
          </p>
        </li>
      ))}
    </ul>
  )
}
